import { useCallback, useEffect, useRef } from 'react';
import { useSetAtom } from 'jotai';
import {
  errorMessageAtom,
  successMessageAtom,
} from '../../atoms/messageBarAtoms';
import {
  deletePricingParameter,
  type IPricingParameter,
  type PricingParameterScope,
} from '../../services/pricingParameterService';
import {
  getParameterErrorMessage,
  parameterLabel,
} from './pricingParameterHelpers';

export const useDeletePricingParameter = (
  scope: PricingParameterScope,
  onDeleted: () => void
) => {
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const pending = useRef<AbortController | null>(null);

  useEffect(() => () => pending.current?.abort(), []);

  return useCallback(
    (record: IPricingParameter) => {
      pending.current?.abort();
      const abortController = new AbortController();
      pending.current = abortController;
      deletePricingParameter(abortController, scope, record.id)
        .then(() => {
          if (abortController.signal.aborted) return;
          setSuccessMessage(
            `Pricing parameter ${parameterLabel(record.key)} deleted.`
          );
          onDeleted();
        })
        .catch((error: unknown) => {
          if (abortController.signal.aborted) return;
          setErrorMessage(
            getParameterErrorMessage(
              error,
              'Failed to delete pricing parameter.'
            )
          );
        });
    },
    [scope, onDeleted, setSuccessMessage, setErrorMessage]
  );
};
